import React from 'react';
import { GetServerSideProps } from 'next';
import dynamic from 'next/dynamic';
import { NAV_DATA } from 'constants/navConstants';
import {
  SecondaryNavIdentifier,
  TertiaryNavIdentifier,
} from '@zola-helpers/client/dist/es/constants/navConstants';
import { fetchUser } from 'api/server/users';
import { initializeStore } from 'src/store/nextStore';
import { receiveUser } from 'actions/UserActions';
import { fetchSuiteCollections } from 'util/fetchSuiteCollections';
import { isPostAuthNav3MobileOrDesktop } from 'util/isPostAuthNav3Active';

import { DayOf, DayOfProps } from 'pages/DayOf';
import {
  TableNumberCollections,
  PlaceCardCollections,
  MenusCollections,
  ProgramsCollections,
} from 'pages/DayOf/collections';

const { PRIMARY } = NAV_DATA;

const Footer = dynamic(
  () => import('@zola/zola-ui/src/components/Footer').then(module => module.Footer),
  { ssr: false }
);

const UnifiedNav = dynamic(() => import('components/common/header/UnifiedNav'), {
  loading: () => <div style={{ height: '140px' }} />,
  ssr: false,
});

export const Page: React.FC<DayOfProps> = props => {
  const isPostAuthNav3 = isPostAuthNav3MobileOrDesktop(props.userContext);

  return (
    <div>
      <div className="container-fluid">
        <div className="row">
          <UnifiedNav
            secondaryType="invites"
            activePrimaryLink={PRIMARY.PAPER}
            activeSecondaryLink={
              isPostAuthNav3 ? SecondaryNavIdentifier.PAPER_SHOP : SecondaryNavIdentifier.DAY_OF
            }
            activeTertiaryLink={TertiaryNavIdentifier.DAY_OF}
            emitDirectionEvents
          />
        </div>
      </div>
      <DayOf {...props} />
      <Footer />
    </div>
  );
};

export const getServerSideProps: GetServerSideProps = async context => {
  const { req } = context;

  const reduxStore = initializeStore();
  const { dispatch, getState } = reduxStore;

  const userContextPromise = fetchUser(req);

  const fetchTableNumberSuitesPromise = fetchSuiteCollections(TableNumberCollections);
  const fetchPlaceCardSuitesPromise = fetchSuiteCollections(PlaceCardCollections);
  const fetchMenusSuitesPromise = fetchSuiteCollections(MenusCollections);
  const fetchProgramsSuitesPromise = fetchSuiteCollections(ProgramsCollections);

  const [
    userContext,
    tableNumberSuitesData,
    placeCardSuitesData,
    menusSuitesData,
    programsSuitesData,
  ] = await Promise.all([
    userContextPromise,
    fetchTableNumberSuitesPromise,
    fetchPlaceCardSuitesPromise,
    fetchMenusSuitesPromise,
    fetchProgramsSuitesPromise,
  ]);

  if (userContext) {
    dispatch(receiveUser(userContext));
  }

  const initialReduxState = JSON.stringify(getState());

  return {
    props: {
      tableNumberSuites: tableNumberSuitesData.suites,
      placeCardSuites: placeCardSuitesData.suites,
      menusSuites: menusSuitesData.suites,
      programsSuites: programsSuitesData.suites,
      initialReduxState,
      userContext,
    },
  };
};

export default Page;
